import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  scheduleCycle as scheduleCycleAction,
  resetSuccess,
} from "./CycleScheduler.action";
import Container from "react-bootstrap/Container";
import { useNavigate } from "react-router-dom";
import DateTimePicker from "components/DateTimePicker/DateTimePicker";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import FormLabel from "react-bootstrap/FormLabel";
import FormGroup from "react-bootstrap/FormGroup";
import FormControl from "react-bootstrap/FormControl";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Alert from "react-bootstrap/Alert";
import SwitchCycleList from "views/Switch/SwitchCycleList/SwitchCycleList";
import ReactBadge from "react-bootstrap/Badge";
import Select from "react-select";
import { showNotification } from "services/NotificationService";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import MessageBanner from "../../../components/MessageBanner";

const Tip = (props) => (
  <div style={{ textAlign: "right", color: "#000000", fontSize: "0.9em" }}>
    <em>{props.children}</em>
  </div>
);

const customStyles = {
  multiValue: (base) => ({
    background: "#007BBB",
    display: "inline-flex",
    color: "white",
    borderRadius: "11px",
    alignItems: "center",
  }),
  multiValueLabel: (base) => ({
    fontSize: "13px",
    color: "white",
    padding: "2px 4px 2px 4px",
  }),
};

const CycleScheduler = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    error: reduxError,
    submittingRequest,
    isSuccess,
    switchList: reduxSwitchList,
    disableAction,
  } = useSelector((state) => ({
    error: state.cycleScheduler.error,
    submittingRequest: state.cycleScheduler.submittingRequest,
    isSuccess: state.cycleScheduler.isSuccess,
    switchList: state.cycleScheduler.switchList,
    disableAction: state.cycleScheduler.disableAction,
  }));

  const [description, setDescription] = useState("");
  const [scheduledDate, setScheduledDate] = useState(new Date());
  const [error, setError] = useState(reduxError);
  const [switchList, setSwitchList] = useState(reduxSwitchList || []);
  const [noOfSelectedSwitches, setNoOfSelectedSwitches] = useState(
    reduxSwitchList ? reduxSwitchList.length : 0
  );
  const [clicked, setClicked] = useState(false);
  const [fetchSwitchList, setFetchSwitchList] = useState(false);
  const [selectedClientNameAbbreviation, setSelectedClientNameAbbreviation] =
    useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    clientNameAbbreviation: [],
    customerNameAbbreviation: [],
    populationType: null,
  });
  const [filterSelectMap, setFilterSelectMap] = useState({});
  const [originalFilterState, setOriginalFilterState] = useState({});
  const [isShowBioSimilarToggle, setIsShowBioSimilarToggle] = useState(false);
  const [clientDetailsMap, setClientDetailsMap] = useState({});

  const setBioSimilarSwitchesToggle = (value) => {
    setIsShowBioSimilarToggle(value);
  };

  useEffect(() => {
    setError(reduxError);
    setSwitchList(reduxSwitchList || []);
    setNoOfSelectedSwitches(reduxSwitchList ? reduxSwitchList.length : 0);
  }, [reduxError, reduxSwitchList]);

  useEffect(() => {
    if (isSuccess) {
      showNotification({
        title: "Cycle Created!",
        message: "Cycle is scheduled successfully.",
        position: "bl",
        type: "success",
      });
      dispatch(resetSuccess());
      navigate("/scheduler/cycle/list");
    }
  }, [isSuccess, dispatch, navigate]);


  const updateFilters = useCallback((tempFilters) => {
    let tempFilterSelectMap = {};
    let clientDetailsMap = {};

    Object.keys(tempFilters).forEach((colKey) => {
      // Create a map of client abbreviations to their full details for easy lookup
      if (colKey === "clientNameAbbreviation") {
        tempFilters[colKey].forEach((client) => {
          clientDetailsMap[client.value] = client;
        });
      }
      // Format data for react-select dropdowns
      tempFilterSelectMap[colKey] = tempFilters[colKey].map((row) => ({
        value: row.id,
        label: row.value,
      }));
    });

    setFilterSelectMap(tempFilterSelectMap);
    setOriginalFilterState({ ...tempFilterSelectMap });
    setClientDetailsMap(clientDetailsMap);
  }, []);

  const fetchData = useCallback(() => {
    sendAuthenticatedAsyncRequest(
      `/swim/batch/get-client-customer-filters`,
      "GET",
      null,
      (r) => {
        if (r.data.filters !== null) {
          updateFilters(r.data.filters);
        }
      },
      (r) => {
        showNotification({
          title: "Error fetching Filter Values!",
          message: "Something went Wrong.",
          position: "tr",
          type: "error",
        });
      }
    );
  }, [updateFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "description") {
      setDescription(value);
    }
    setError("");
  };

  const validateForm = (scheduledDate, switchList, description) => {
    var currentDate = new Date();
    if (scheduledDate < currentDate)
      return "Date and Time should be greater than current Time";
    if (switchList.length === 0) return "Switch list can not be empty";
    if (description && description.length > 100)
      return "Description can not exceed 100 characters in length";
    return null;
  };

  const handleScheduleCycle = (e) => {
    const clientList = selectedFilters.clientNameAbbreviation.reduce(
      (result, entry) => {
        return result.concat(entry.value);
      },
      []
    );
    var validationMessage = validateForm(
      scheduledDate,
      switchList,
      description
    );
    if (validationMessage === null) { 
      const cycleDto = {
        scheduledDate: scheduledDate,
        description: description,
        wfItemUuids: switchList,
        clientList: clientList,
      };
      dispatch(scheduleCycleAction(cycleDto));
    } else {
      setError(validationMessage);
    }
  };

  const setSelectedSwitches = useCallback((selected) => {
    setSwitchList(selected);
    setNoOfSelectedSwitches(selected.length);
  }, []);

  const switchIdsSectionClicked = () => {
    setClicked(!clicked);
  };

  const removeSwitch = (switchId) => {
    let index = switchList.indexOf(switchId);
    if (index > -1) {
      const newSwitchList = [...switchList];
      newSwitchList.splice(index, 1);
      setSwitchList(newSwitchList);
      setNoOfSelectedSwitches(newSwitchList.length);
    }
  };

  const handleClientChange = (option) => {
    const updatedClientList = option !== null ? option : [];

    setSwitchList([]);
    setNoOfSelectedSwitches(0);
    setSelectedClientNameAbbreviation([]);
    setFetchSwitchList(false);

    setSelectedFilters((prevState) => ({
      ...prevState,
      clientNameAbbreviation: updatedClientList,
    }));
  };
  
  const handleCustomerChange = (option) => {
    const updatedCustomerList = option ? [option] : [];
    let filteredClientOptions =
      originalFilterState.clientNameAbbreviation || [];
    let populationTypeOptions = [];

    if (option) {
      // Find all client labels associated with the selected customer
      const customerToClientsMap =
        filterSelectMap["customerClientMapping"] || [];
      const mapping = customerToClientsMap.find((m) => m.value === option.label);
      const clientLabelsForCustomer = mapping ? mapping.label : [];

      // Filter the master list of clients
      filteredClientOptions =
        originalFilterState.clientNameAbbreviation.filter((client) =>
          clientLabelsForCustomer.includes(client.label)
        );

      // From these filtered clients, find all unique population types
      const uniquePopulationTypes = new Set();
      filteredClientOptions.forEach((clientOption) => {
        const clientDetail = clientDetailsMap[clientOption.label];
        if (clientDetail && clientDetail.populationType) {
          uniquePopulationTypes.add(clientDetail.populationType);
        }
      });

      populationTypeOptions = Array.from(uniquePopulationTypes).map((pt) => ({
        value: pt,
        label: pt,
      }));
    }

    // Update state
    setSelectedFilters({
      customerNameAbbreviation: updatedCustomerList,
      populationType: null, // Reset on customer change
      clientNameAbbreviation: [], // Reset on customer change
    });
    setFilterSelectMap((prevState) => ({
      ...prevState,
      clientNameAbbreviation: filteredClientOptions,
      populationType: populationTypeOptions,
    }));
    setSwitchList([]);
    setNoOfSelectedSwitches(0);
    setFetchSwitchList(false);
    setSelectedClientNameAbbreviation([]);
  };

  const handlePopulationTypeChange = (option) => {
    const selectedCustomer = selectedFilters.customerNameAbbreviation[0];
    let clientOptionsForCustomer = [];

    // First, determine the pool of clients based on the selected customer
    if (selectedCustomer) {
      const customerToClientsMap =
        filterSelectMap["customerClientMapping"] || [];
      const mapping = customerToClientsMap.find(
        (m) => m.value === selectedCustomer.label
      );
      const clientLabelsForCustomer = mapping ? mapping.label : [];
      clientOptionsForCustomer =
        originalFilterState.clientNameAbbreviation.filter((client) =>
          clientLabelsForCustomer.includes(client.label)
        );
    } else {
      // Fallback to all clients if no customer is selected (though UI should prevent this)
      clientOptionsForCustomer = originalFilterState.clientNameAbbreviation || [];
    }

    let finalClientOptions = clientOptionsForCustomer;

    // If a population type is selected, filter the client pool further
    if (option) {
      const selectedPopulationType = option.value;
      finalClientOptions = clientOptionsForCustomer.filter((clientOption) => {
        const clientDetail = clientDetailsMap[clientOption.label];
        return clientDetail && clientDetail.populationType === selectedPopulationType;
      });
    }

    // Update state
    setSelectedFilters((prevState) => ({
      ...prevState,
      populationType: option,
      clientNameAbbreviation: [], // Reset client selection
    }));
    setFilterSelectMap((prevState) => ({
      ...prevState,
      clientNameAbbreviation: finalClientOptions,
    }));
    setSwitchList([]);
    setNoOfSelectedSwitches(0);
    setFetchSwitchList(false);
    setSelectedClientNameAbbreviation([]);
  };

  const addClientsToCustomers = (
    customerListArray = selectedFilters.customerNameAbbreviation,
    clientListArray = selectedFilters.clientNameAbbreviation
  ) => {
    let extractedSelectedClients = clientListArray;

    if (clientListArray.length === 0) {
      // Get possible list of clients for customer selected
      const appliedCustomerFilters = customerListArray.map(
        (customer) => customer.label
      );
      const extractedSelectedCustomers = filterSelectMap[
        "customerClientMapping"
      ].filter((e) => appliedCustomerFilters.includes(e.value));
      const extractedClients = extractedSelectedCustomers.reduce(
        (result, entry) => {
          return result.concat(entry.label);
        },
        []
      );
      extractedSelectedClients = originalFilterState.clientNameAbbreviation.filter((item) =>
          new Set(extractedClients).has(item.label)
        );
    }

    return extractedSelectedClients;
  };

  const searchSwitches = () => {
    let reducedClientListArray = addClientsToCustomers();
    reducedClientListArray = reducedClientListArray.reduce((result, entry) => {
      return result.concat(entry.label);
    }, []);
    setSelectedClientNameAbbreviation(reducedClientListArray);
    setFetchSwitchList(true);
  };

  const onDateChange = (date) => {
    setScheduledDate(date);
    setError("");
  };

    const populationType = selectedFilters.populationType
      ? selectedFilters.populationType.value
      : null;
    return (
      <Container fluid className="top-spacer">
        <Row>
          <Col md={{ span: 15 }}>
            <MessageBanner
              message={
                "You cannot schedule a new Cycle as one is already being processed"
              }
              isDisplayMessage={disableAction}
              icon={faExclamationCircle}
            />
            <div>
              <Card variant="primary">
                <Card.Header>
                  <Card.Title className="text-center">
                    <h4>Scheduler</h4>
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  {error ? <Alert variant="danger">{error}</Alert> : null}
                  {submittingRequest ? (
                    <Alert variant="primary">{submittingRequest}</Alert>
                  ) : null}
                  <Row>
                    <Col md={12}>
                      <Tip>Enter Date & Time according to your Time Zone</Tip>
                    </Col>
                    <Col md={12}>
                      <FormGroup className="tiny-top-spacer" as={Row}>
                        <Col md={3}>
                          <FormLabel>Date & Time</FormLabel>
                        </Col>
                        <Col md={9}>
                          <DateTimePicker
                            onChange={onDateChange}
                            value={scheduledDate}
                            disabled={disableAction}
                          />
                        </Col>
                      </FormGroup>
                      <FormGroup className="tiny-top-spacer" as={Row}>
                        <Col md={3}>
                          <FormLabel>Description </FormLabel>
                        </Col>
                        <Col md={9}>
                          <FormControl
                            type="text"
                            name="description"
                            value={description}
                            placeholder="Enter Description (optional)"
                            onChange={handleChange}
                            disabled={disableAction}
                          />
                        </Col>
                      </FormGroup>
                      <FormGroup className="tiny-top-spacer" as={Row}>
                        <Col md={3}>
                          <FormLabel>Customer </FormLabel>
                        </Col>
                        <Col md={9}>
                          <Select
                            options={
                              Object.keys(filterSelectMap).includes(
                                "customerNameAbbreviation"
                              )
                                ? filterSelectMap["customerNameAbbreviation"]
                                : []
                            }
                            value={selectedFilters.customerNameAbbreviation}
                            onChange={handleCustomerChange}
                            placeholder={selectedFilters.clientNameAbbreviation.length === 0 ? "Enter Customer (Mandatory)" : "Enter Customer (Optional)"}
                            isMulti={false} // Assuming single customer selection
                            isClearable
                            styles={customStyles}
                          ></Select>
                        </Col>
                      </FormGroup>

                      {/* NEW DROPDOWN START: Population Type dropdown */}
                      <FormGroup className="tiny-top-spacer" as={Row}>
                        <Col md={3}>
                          <FormLabel>Population Type</FormLabel>
                        </Col>
                        <Col md={9}>
                          <Select
                            options={filterSelectMap.populationType || []}
                            value={selectedFilters.populationType}
                            onChange={handlePopulationTypeChange}
                            placeholder="Select Population Type (Optional)"
                            isClearable
                            isDisabled={
                              selectedFilters.customerNameAbbreviation.length === 0
                            }
                            styles={customStyles}
                          />
                        </Col>
                      </FormGroup>
                      {/* NEW DROPDOWN END */}

                      <FormGroup className="tiny-top-spacer" as={Row}>
                        <Col md={3}>
                          <FormLabel>Client </FormLabel>
                        </Col>
                        <Col md={9}>
                          <Select
                            options={
                              Object.keys(filterSelectMap).includes(
                                "clientNameAbbreviation"
                              )
                                ? filterSelectMap["clientNameAbbreviation"]
                                : []
                            }
                            value={selectedFilters.clientNameAbbreviation}
                            onChange={handleClientChange}
                            isMulti
                                                        placeholder={selectedFilters.customerNameAbbreviation.length === 0 ? "Enter Client (Mandatory)" : "Enter Client (Optional)"}
                            closeMenuOnSelect={false}
                            styles={customStyles}
                          ></Select>
                        </Col>
                      </FormGroup>
                      <FormGroup className="tiny-top-spacer" as={Row}>
                        <Col md={3}>
                          <FormLabel>No. of Selected Switches </FormLabel>
                        </Col>
                        <Col md={9}>
                          <ReactBadge
                            id="no-of-selected-switches"
                            variant="success"
                            pill
                            style={{ margin: "-1px 5px", fontSize: "100%" }}
                          >
                            {noOfSelectedSwitches ? noOfSelectedSwitches : 0}
                          </ReactBadge>
                        </Col>
                      </FormGroup>
                    </Col>
                    <Col
                      md={{ span: 3, offset: 9 }}
                      className="d-grid gap-2 mt-2"
                    >
                      {fetchSwitchList === true ? (
                        <Button
                          className="btn-block btn-text-white"
                          variant="success"
                          onClick={handleScheduleCycle}
                          disabled={
                            submittingRequest ||
                            disableAction ||
                            switchList.length === 0
                          }
                        >
                          Schedule Cycle
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          onClick={searchSwitches}
                          disabled={
                            submittingRequest ||
                            disableAction ||
                            (selectedFilters.clientNameAbbreviation.length ===
                              0 &&
                              selectedFilters.customerNameAbbreviation.length ===
                                0)
                          }
                        >
                          Search
                        </Button>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </div>
          </Col>
          {disableAction ||
          (fetchSwitchList === false &&
            selectedClientNameAbbreviation.length === 0) ? null : (
            <SwitchCycleList
              selectedClientNameAbbreviation={selectedClientNameAbbreviation}
              populationType={populationType}
              selected={switchList}
              setSelectedSwitches={setSelectedSwitches}
              moveToTheListView={clicked}
              bioSimilarSwitchesToggle={isShowBioSimilarToggle}
              setBioSimilarSwitchesToggle={setBioSimilarSwitchesToggle}
              toggleDefaultFilters={searchSwitches}
            />
          )}
        </Row>
      </Container>
    );
};

export default CycleScheduler;